import React, { useState, useEffect } from 'react';
import {
  Send,
  Users,
  Upload,
  Download,
  CheckCircle,
  XCircle,
  Clock,
  Plus,
  Trash2,
  Eye,
  Copy
} from 'lucide-react';

interface TokenTransfer {
  id: string;
  to: string;
  amount: number;
  token: 'VTTN' | 'VTM';
  status: 'pending' | 'processing' | 'completed' | 'failed';
  txHash?: string;
  timestamp: string;
  gasUsed?: number;
  gasFee?: number;
}

interface BulkTransfer {
  id: string;
  name: string;
  totalRecipients: number;
  completedTransfers: number;
  failedTransfers: number;
  totalAmount: number;
  token: 'VTTN' | 'VTM';
  status: 'pending' | 'processing' | 'completed' | 'failed';
  createdAt: string;
  transfers: TokenTransfer[];
}

interface WalletBalance {
  VTTN: number;
  VTM: number;
}

const TokenTransferPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'single' | 'bulk' | 'history'>('single');
  const [balance, setBalance] = useState<WalletBalance>({ VTTN: 0, VTM: 0 });
  const [transfers, setTransfers] = useState<TokenTransfer[]>([]);
  const [bulkTransfers, setBulkTransfers] = useState<BulkTransfer[]>([]);
  const [loading, setLoading] = useState(true);

  // Single transfer form
  const [singleTransfer, setSingleTransfer] = useState({
    to: '',
    amount: '',
    token: 'VTTN' as 'VTTN' | 'VTM',
    memo: ''
  });

  // Bulk transfer
  const [bulkRecipients, setBulkRecipients] = useState<Array<{to: string, amount: string}>>([{to: '', amount: ''}]);
  const [bulkToken, setBulkToken] = useState<'VTTN' | 'VTM'>('VTTN');

  useEffect(() => {
    loadTransferData();
    const interval = setInterval(loadTransferData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadTransferData = async () => {
    try {
      // Mock data - 실제로는 API 호출
      const mockBalance: WalletBalance = {
        VTTN: 125000,
        VTM: 50000
      };

      const mockTransfers: TokenTransfer[] = [
        {
          id: 'tx-001',
          to: '0x1234...5678',
          amount: 1000,
          token: 'VTTN',
          status: 'completed',
          txHash: '0xabc123...def456',
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          gasUsed: 21000,
          gasFee: 0.5
        },
        {
          id: 'tx-002',
          to: '0x2345...6789',
          amount: 500,
          token: 'VTM',
          status: 'processing',
          timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString()
        },
        {
          id: 'tx-003',
          to: '0x3456...7890',
          amount: 2000,
          token: 'VTTN',
          status: 'failed',
          timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString()
        }
      ];

      const mockBulkTransfers: BulkTransfer[] = [
        {
          id: 'bulk-001',
          name: 'Airdrop Campaign #1',
          totalRecipients: 100,
          completedTransfers: 95,
          failedTransfers: 5,
          totalAmount: 50000,
          token: 'VTTN',
          status: 'completed',
          createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          transfers: []
        },
        {
          id: 'bulk-002',
          name: 'Referral Rewards',
          totalRecipients: 50,
          completedTransfers: 25,
          failedTransfers: 0,
          totalAmount: 12500,
          token: 'VTTN',
          status: 'processing',
          createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          transfers: []
        }
      ];

      setBalance(mockBalance);
      setTransfers(mockTransfers);
      setBulkTransfers(mockBulkTransfers);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load transfer data:', error);
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-50 border-green-200';
      case 'processing': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'pending': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'failed': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'processing': return <Clock className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'failed': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ko-KR', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleSingleTransfer = async () => {
    if (!singleTransfer.to || !singleTransfer.amount) {
      alert('Please fill in all required fields');
      return;
    }

    // 실제로는 API 호출
    console.log('Single transfer:', singleTransfer);
    alert('Transfer initiated!');
    setSingleTransfer({ to: '', amount: '', token: 'VTTN', memo: '' });
  };

  const handleBulkTransfer = async () => {
    const validRecipients = bulkRecipients.filter(r => r.to && r.amount);
    if (validRecipients.length === 0) {
      alert('Please add at least one valid recipient');
      return;
    }

    // 실제로는 API 호출
    console.log('Bulk transfer:', { recipients: validRecipients, token: bulkToken });
    alert('Bulk transfer initiated!');
    setBulkRecipients([{to: '', amount: ''}]);
  };

  const addBulkRecipient = () => {
    setBulkRecipients([...bulkRecipients, {to: '', amount: ''}]);
  };

  const removeBulkRecipient = (index: number) => {
    setBulkRecipients(bulkRecipients.filter((_, i) => i !== index));
  };

  const updateBulkRecipient = (index: number, field: 'to' | 'amount', value: string) => {
    const updated = [...bulkRecipients];
    updated[index][field] = value;
    setBulkRecipients(updated);
  };

  const handleCsvUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // 실제로는 CSV 파싱 로직
      console.log('CSV file uploaded:', file.name);
      // CSV 파일 처리 로직 추가 예정
    }
  };

  const downloadCsvTemplate = () => {
    const csvContent = "address,amount\n0x1234567890123456789012345678901234567890,100\n0x2345678901234567890123456789012345678901,200";
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk_transfer_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Token Transfer</h1>
          <p className="text-gray-600 mt-2">Send tokens to single or multiple recipients</p>
        </div>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">VTTN Balance</p>
              <p className="text-2xl font-bold text-orange-600">{balance.VTTN.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <Send className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">VTM Balance</p>
              <p className="text-2xl font-bold text-blue-600">{balance.VTM.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Send className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="card">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'single', name: 'Single Transfer', icon: Send },
              { id: 'bulk', name: 'Bulk Transfer', icon: Users },
              { id: 'history', name: 'Transfer History', icon: Eye }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Single Transfer Tab */}
          {activeTab === 'single' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Send Tokens</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Recipient Address *
                  </label>
                  <input
                    type="text"
                    placeholder="0x..."
                    value={singleTransfer.to}
                    onChange={(e) => setSingleTransfer({...singleTransfer, to: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Token
                  </label>
                  <select
                    value={singleTransfer.token}
                    onChange={(e) => setSingleTransfer({...singleTransfer, token: e.target.value as 'VTTN' | 'VTM'})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                  >
                    <option value="VTTN">VTTN</option>
                    <option value="VTM">VTM</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount *
                </label>
                <div className="relative">
                  <input
                    type="number"
                    placeholder="0.00"
                    value={singleTransfer.amount}
                    onChange={(e) => setSingleTransfer({...singleTransfer, amount: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <span className="text-gray-500 text-sm">{singleTransfer.token}</span>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Available: {balance[singleTransfer.token].toLocaleString()} {singleTransfer.token}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Memo (Optional)
                </label>
                <textarea
                  placeholder="Add a note for this transfer..."
                  value={singleTransfer.memo}
                  onChange={(e) => setSingleTransfer({...singleTransfer, memo: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                />
              </div>
              
              <button
                onClick={handleSingleTransfer}
                className="btn-primary flex items-center space-x-2"
              >
                <Send className="w-4 h-4" />
                <span>Send Transfer</span>
              </button>
            </div>
          )}

          {/* Bulk Transfer Tab */}
          {activeTab === 'bulk' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Bulk Transfer</h3>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={downloadCsvTemplate}
                    className="btn-secondary flex items-center space-x-2"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download Template</span>
                  </button>
                  <label className="btn-secondary flex items-center space-x-2 cursor-pointer">
                    <Upload className="w-4 h-4" />
                    <span>Upload CSV</span>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleCsvUpload}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Token
                </label>
                <select
                  value={bulkToken}
                  onChange={(e) => setBulkToken(e.target.value as 'VTTN' | 'VTM')}
                  className="w-full md:w-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                >
                  <option value="VTTN">VTTN</option>
                  <option value="VTM">VTM</option>
                </select>
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Recipients ({bulkRecipients.filter(r => r.to && r.amount).length})
                  </label>
                  <button
                    onClick={addBulkRecipient}
                    className="btn-secondary flex items-center space-x-2"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Recipient</span>
                  </button>
                </div>
                
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {bulkRecipients.map((recipient, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="flex-1">
                        <input
                          type="text"
                          placeholder="Recipient address (0x...)"
                          value={recipient.to}
                          onChange={(e) => updateBulkRecipient(index, 'to', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                        />
                      </div>
                      <div className="w-32">
                        <input
                          type="number"
                          placeholder="Amount"
                          value={recipient.amount}
                          onChange={(e) => updateBulkRecipient(index, 'amount', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                        />
                      </div>
                      {bulkRecipients.length > 1 && (
                        <button
                          onClick={() => removeBulkRecipient(index)}
                          className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span>Total Recipients:</span>
                  <span className="font-medium">{bulkRecipients.filter(r => r.to && r.amount).length}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span>Total Amount:</span>
                  <span className="font-medium">
                    {bulkRecipients.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0).toLocaleString()} {bulkToken}
                  </span>
                </div>
              </div>
              
              <button
                onClick={handleBulkTransfer}
                className="btn-primary flex items-center space-x-2"
              >
                <Users className="w-4 h-4" />
                <span>Send Bulk Transfer</span>
              </button>
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Transfer History</h3>
              
              {/* Recent Transfers */}
              <div>
                <h4 className="font-medium text-gray-900 mb-4">Recent Transfers</h4>
                <div className="space-y-3">
                  {transfers.map((transfer) => (
                    <div key={transfer.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-4">
                        <div className="p-2 bg-white rounded-lg">
                          <Send className="w-4 h-4 text-gray-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {transfer.amount.toLocaleString()} {transfer.token}
                          </p>
                          <p className="text-sm text-gray-500">To: {transfer.to}</p>
                          <p className="text-sm text-gray-500">{formatDate(transfer.timestamp)}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium border ${getStatusColor(transfer.status)}`}>
                          {getStatusIcon(transfer.status)}
                          <span className="capitalize">{transfer.status}</span>
                        </span>
                        {transfer.txHash && (
                          <button
                            onClick={() => copyToClipboard(transfer.txHash!)}
                            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Bulk Transfer History */}
              <div>
                <h4 className="font-medium text-gray-900 mb-4">Bulk Transfer History</h4>
                <div className="space-y-3">
                  {bulkTransfers.map((bulk) => (
                    <div key={bulk.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h5 className="font-medium text-gray-900">{bulk.name}</h5>
                          <p className="text-sm text-gray-500">{formatDate(bulk.createdAt)}</p>
                        </div>
                        <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium border ${getStatusColor(bulk.status)}`}>
                          {getStatusIcon(bulk.status)}
                          <span className="capitalize">{bulk.status}</span>
                        </span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Recipients:</span>
                          <span className="font-medium ml-1">{bulk.totalRecipients}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Completed:</span>
                          <span className="font-medium ml-1 text-green-600">{bulk.completedTransfers}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Failed:</span>
                          <span className="font-medium ml-1 text-red-600">{bulk.failedTransfers}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Total Amount:</span>
                          <span className="font-medium ml-1">{bulk.totalAmount.toLocaleString()} {bulk.token}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TokenTransferPage;